import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { TrustRuleResponse } from "./api";
import { TrustRuleFormModal } from "./TrustRuleFormModal";
import { useDeleteTrustRule } from "./useDeleteTrustRule";
import { useTrustRules } from "./useTrustRules";

// Секция «Trust Rules» на `/settings/trust-rules`.
//
// Таблица правил + CRUD: «Создать» открывает форму-модалку (editing=null),
// «Изменить» — её же с префиллом, «Удалить» — Modal-confirm.
export function TrustRulesSection(): ReactElement {
  const { t } = useTranslation();
  const rules = useTrustRules();
  const deleteMutation = useDeleteTrustRule();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TrustRuleResponse | null>(null);
  const [deleting, setDeleting] = useState<TrustRuleResponse | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (rule: TrustRuleResponse): void => {
    setEditing(rule);
    setFormOpen(true);
  };
  const closeForm = (): void => {
    setFormOpen(false);
  };
  // Один handler на кнопку «Отмена» и на Modal.onClose (Esc / backdrop) —
  // иначе вторая ветка осталась бы непокрытой (приём из ForgejoSection).
  const cancelDelete = (): void => {
    setDeleting(null);
  };

  if (rules.isPending) {
    return <Loader />;
  }

  if (rules.isError) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("trustRules.title")}</Title>
        <Alert color="red" title={t("trustRules.errorHeading")}>
          {t("trustRules.loadingError")}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t("trustRules.title")}</Title>
        <Button onClick={openCreate}>{t("trustRules.createButton")}</Button>
      </Group>

      {rules.data.length === 0 ? (
        <Text c="dimmed">{t("trustRules.empty")}</Text>
      ) : (
        <Table withTableBorder withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("trustRules.colName")}</Table.Th>
              <Table.Th>{t("trustRules.colPaths")}</Table.Th>
              <Table.Th>{t("trustRules.colActions")}</Table.Th>
              <Table.Th>{t("trustRules.colActive")}</Table.Th>
              <Table.Th>{t("trustRules.colManage")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rules.data.map((rule) => (
              <Table.Tr key={rule.id}>
                <Table.Td>{rule.name}</Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {rule.paths_json.join(", ")}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {rule.auto_approve_json.length === 0 ? (
                    <Text c="dimmed" size="sm">
                      {t("trustRules.actionsManual")}
                    </Text>
                  ) : (
                    <Group gap="xs">
                      {rule.auto_approve_json.map((action) => (
                        <Badge key={action} variant="light">
                          {t(`trustRules.action.${action}`)}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Table.Td>
                <Table.Td>
                  {rule.is_active ? (
                    <Badge color="green">{t("trustRules.activeYes")}</Badge>
                  ) : (
                    <Badge color="gray">{t("trustRules.activeNo")}</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => {
                        openEdit(rule);
                      }}
                    >
                      {t("trustRules.editButton")}
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      variant="subtle"
                      onClick={() => {
                        setDeleting(rule);
                      }}
                    >
                      {t("trustRules.deleteButton")}
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <TrustRuleFormModal
        opened={formOpen}
        onClose={closeForm}
        editing={editing}
      />

      <Modal
        opened={deleting !== null}
        onClose={cancelDelete}
        title={t("trustRules.delete.confirmTitle")}
        centered
      >
        {deleting !== null ? (
          <Stack gap="md">
            <Text>
              {t("trustRules.delete.confirmText", { name: deleting.name })}
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={cancelDelete}>
                {t("trustRules.modalCancel")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  deleteMutation.mutate(deleting.id, {
                    onSuccess: () => {
                      setDeleting(null);
                    },
                  });
                }}
                loading={deleteMutation.isPending}
              >
                {t("trustRules.delete.confirmButton")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}
